<template>
    <activix-modal
        :size="isExternalCall ? 'md' : 'sm'"
        :name="name"
        :portal="portal"
        :loading="loading"
        :loading-overlay="false"
        ref="modal"
        @close="invalidateOldCommunication"
        @closed="onClosed"
        @before-open="onBeforeOpen"
    >
        <template #header>
            <h4 class="modal-title" v-if="isExternalCall">
                {{ $t('call.contactInfo') }}
            </h4>
            <h4 class="modal-title" v-else>
                {{ callInProgress ? $t('call.callInProgress') : $t('call.generate') }}
            </h4>
        </template>

        <template #body>
            <div class="text-center" data-testid="generate-call-modal">
                <next-automation-alert :lead="lead" />

                <communicating-alert
                    assignment-trace-source="desktop_call_notify"
                    :lead="lead"
                    :communicating-type="communicatingType"
                    :communicating-user="communicatingUser"
                    :recent-communication="recentCommunication"
                    :most-recent-communication="mostRecentCommunication"
                    @dismiss="dismiss"
                    @close="close"
                    v-if="!callInProgress"
                />

                <call-lead-external
                    :lead-phone-options="leadPhoneOptions"
                    :communicating="!!communicatingLead || checkingCommunicating"
                    :call-in-progress="callInProgress"
                    :loading="loading"
                    :recording-consent-script="callRecordingConsentScript"
                    v-if="isExternalCall"
                />

                <call-lead-internal
                    :lead-phone-options="leadPhoneOptions"
                    :user-phone-options="userPhoneOptions"
                    :communicating="!!communicatingLead || checkingCommunicating"
                    :call-in-progress="callInProgress"
                    :loading="loading"
                    :lead="lead"
                    :recording-consent-script="callRecordingConsentScript"
                    v-model:lead-phone="leadPhone"
                    v-model:user-phone="userPhone"
                    v-else
                />

                <div class="mt-4" v-if="callInProgress && showLinkToClientCard">
                    <router-link
                        :to="{ name: 'leads.update', params: { id: lead.id } }"
                        target="_blank"
                    >
                        {{ $t('general.goToClientCard') }}
                    </router-link>
                </div>
            </div>
        </template>

        <template #footer v-if="!isExternalCall">
            <div class="flex items-center justify-center">
                <activix-button data-testid="generate-call-cancel-button" @click="close">
                    {{ $t('general.cancel') }}
                </activix-button>

                <activix-button
                    data-testid="generate-call-submit-button"
                    type="primary"
                    :disabled="disabled"
                    @click="makeCall"
                    v-if="!callInProgress || recordingDisabled"
                >
                    {{ $t('client.call') }}
                </activix-button>

                <activix-button
                    type="primary"
                    :disabled="!lead.current_call_sid"
                    @click="startRecording"
                    v-if="
                        callInProgress &&
                        callRecordingConsentScript &&
                        !isRecording &&
                        !lead.current_call_recording_sid &&
                        !recordingDisabled
                    "
                >
                    {{ lead.current_call_sid ? $t('client.startRecording') : $t('client.dialing') }}
                </activix-button>

                <activix-button
                    type="primary"
                    :disabled="!lead.current_call_sid || !lead.current_call_recording_sid"
                    @click="pauseRecording"
                    v-if="
                        callInProgress &&
                        callRecordingConsentScript &&
                        isRecording &&
                        !recordingDisabled
                    "
                >
                    {{ $t('client.pauseRecording') }}
                </activix-button>

                <activix-button
                    type="primary"
                    :disabled="!lead.current_call_sid || !lead.current_call_recording_sid"
                    @click="resumeRecording"
                    v-if="
                        callInProgress &&
                        callRecordingConsentScript &&
                        !isRecording &&
                        lead.current_call_recording_sid &&
                        !recordingDisabled
                    "
                >
                    {{ $t('client.resumeRecording') }}
                </activix-button>

                <div
                    class="absolute right-0 mr-6"
                    v-if="currentUser.block_lead_info && showPhoneCountDown > 0 && !callInProgress"
                >
                    <button class="link-grey" @click="showPhone = true" v-if="!showPhone">
                        <icon name="regular/phone" class="cursor-pointer" />
                    </button>
                    <span class="text-grey-500" v-else>
                        {{ showPhoneCountDown }}
                    </span>
                </div>
            </div>
        </template>
    </activix-modal>
</template>

<script>
    import { useAccountStore } from '@/store/modules/account/store.js';
    import { useContextStore } from '@/store/modules/context/store.ts';
    import { useEventBus } from '@/hooks/useEventBus.ts';
    import { mapActions, mapState } from 'pinia';
    import { useGlobalStore } from '@/store/store.ts';
    import { useUserStore } from '@/store/modules/user/store.js';
    import { invalidateCustomerProfileLocally } from '@/components/customerProfile/invalidation/customerProfileInvalidation.ts';

    import { showWarning } from '@/utils/toastr.js';

    import CallLeadExternal from '@/components/modals/CallLeadExternal.vue';
    import CallLeadInternal from '@/components/modals/CallLeadInternal.vue';
    import CommunicatingAlert from '@/components/CommunicatingAlert.vue';
    import NextAutomationAlert from '@/components/lead/NextAutomationAlert.vue';

    import Lead from '@/entities/Lead.js';
    import PhoneProvider from '@/entities/PhoneProvider.js';
    import User from '@/entities/User.js';

    const { on, off } = useEventBus();

    export default {
        components: {
            CallLeadExternal,
            CallLeadInternal,
            CommunicatingAlert,
            NextAutomationAlert,
        },

        props: {
            name: {
                type: String,
                required: true,
            },
            portal: {
                type: String,
                default: 'modal-1',
            },
        },

        data() {
            return {
                recentCommunication: null,
                mostRecentCommunication: null,
                modalSession: 0,
                oldCommunicationRequest: 0,
                checkingCommunicating: false,
                processing: false,
                showLinkToClientCard: false,
                showPhone: false,
                showPhoneCountDown: 5,
                leadPhone: null,
                userPhone: null,
                callInProgress: false,
                loading: true,
                leadId: null,
                customerContactTarget: null,
                lead: new Lead(),
                userPhones: [],
                currentUser: new User(),
                isRecording: false,
            };
        },

        computed: {
            ...mapState(useGlobalStore, ['specificPhoneToCall', 'taskEventToBeCompleted']),
            ...mapState(useContextStore, ['communicatingLead']),

            parentAuthUser() {
                return useUserStore().parentAuthUser;
            },

            authUser() {
                return useUserStore().authUser;
            },

            contextAccount() {
                return useAccountStore().contextAccount;
            },

            isExternalCall() {
                if (PhoneProvider.REQUIRES_EXTENSION.includes(this.contextAccount.phone_provider)) {
                    return this.isExtensionDectected;
                }

                return PhoneProvider.EXTERNAL_PROVIDERS.includes(
                    this.contextAccount.phone_provider
                );
            },

            isExtensionDectected() {
                return !!this.$chromeExtensions.length;
            },

            userPhoneOptions() {
                return this.userPhones.map((phone) => ({
                    id: phone.id,
                    type: phone.cellphone ? 'cell' : 'work',
                    value: phone.number,
                    label: this.stringifyPhone(phone.number),
                    extension: phone.extension,
                }));
            },

            leadPhoneOptions() {
                if (this.customerContactTarget) {
                    return [
                        {
                            id: this.customerContactTarget.contact_phone_id,
                            lead_id: this.customerContactTarget.lead_id,
                            type: 'cell',
                            value: this.customerContactTarget.destination,
                            label: this.stringifyPhone(
                                this.customerContactTarget.destination,
                                true
                            ),
                            extension: this.customerContactTarget.extension,
                            contact_phone_id: this.customerContactTarget.contact_phone_id,
                        },
                    ];
                }

                return this.lead.getUniquePhones().map((phone) => ({
                    id: phone.id,
                    lead_id: phone.lead_id,
                    type: phone.type,
                    value: phone.phone,
                    label: this.stringifyPhone(phone.phone, true),
                    extension: phone.extension,
                }));
            },

            leadPhoneType() {
                return (
                    this.leadPhoneOptions.find((phone) => phone.id === this.leadPhone)?.type || null
                );
            },

            callDisabled() {
                return !!(
                    this.lead.unsubscribe_call ||
                    this.lead.unsubscribe_all_date ||
                    this.processing ||
                    !!this.communicatingLead ||
                    this.mostRecentCommunication ||
                    this.callInProgress ||
                    this.loading ||
                    !this.leadPhone ||
                    !this.userPhone
                );
            },

            callRecordingConsentScript() {
                return this.contextAccount.call_consent_script || '';
            },

            contextAccountHasSupportedPhoneProvider() {
                return PhoneProvider.INTEGRATED_PROVIDERS.includes(
                    this.contextAccount.phone_provider
                );
            },

            communicatingType() {
                return this.communicatingLead?.communication_method_id;
            },

            communicatingUser() {
                return this.communicatingLead?.communication_user?.id;
            },

            disabled() {
                return (
                    this.callDisabled ||
                    this.callInProgress ||
                    (!this.contextAccountHasSupportedPhoneProvider &&
                        !this.contextAccountHasSupplierPhoneProvider)
                );
            },

            contextAccountHasSupplierPhoneProvider() {
                return PhoneProvider.SUPPLIER_PROVIDERS.includes(
                    this.contextAccount.phone_provider
                );
            },

            recordingDisabled() {
                return !this.contextAccount.call_recording;
            },
        },

        watch: {
            specificPhoneToCall() {
                this.setLeadPhoneType();
            },

            showPhone() {
                if (!this.showPhone) {
                    this.showPhoneCountDown = 5;
                } else {
                    this.reduceCountDown();
                }
            },
        },

        methods: {
            ...mapActions(useGlobalStore, [
                'fetchLead',
                'updateLeadAction',
                'taskEventUpdated',
                'appendNewError',
                'setSpecificPhoneToCall',
                'setTaskEventToBeCompleted',
            ]),
            ...mapActions(useContextStore, ['checkCommunicating']),

            stringifyPhone(phone, block = false) {
                const formattedPhone = this.formatPhone(phone);

                if (block && this.currentUser.block_lead_info && this.showPhoneCountDown > 0) {
                    return `${formattedPhone.slice(0, -4)}XXXX`;
                }

                return formattedPhone;
            },

            reduceCountDown() {
                this.showPhoneCountDown--;

                if (this.showPhoneCountDown > 0) {
                    setTimeout(() => {
                        this.reduceCountDown();
                    }, 1000);
                }
            },

            async makeCall() {
                if (this.callDisabled) {
                    return;
                }

                const customerId = Number(this.lead.customer_id ?? this.lead.customer?.id);
                this.processing = true;

                const payload = {
                    phoneType: this.leadPhoneType,
                    userPhone: this.userPhone,
                    ...(this.customerContactTarget
                        ? { contact_phone_id: this.customerContactTarget.contact_phone_id }
                        : {}),
                };

                try {
                    if (this.contextAccountHasSupplierPhoneProvider) {
                        await this.$axios.post(`v1/ringcentral/ringout/${this.lead.id}`, payload);
                    } else {
                        await this.$axios.post(`v1/voice/outbound/${this.lead.id}`, payload);
                    }

                    invalidateCustomerProfileLocally({
                        customerId,
                        payloads: ['activity'],
                    });

                    // Complete taskEvent if necessary
                    if (this.taskEventToBeCompleted) {
                        this.$axios
                            .post(`v1/reminder/done/${this.taskEventToBeCompleted}`)
                            .then(({ data }) => {
                                if (!data.success) {
                                    return;
                                }

                                this.taskEventUpdated(data.taskEvent);
                                invalidateCustomerProfileLocally({
                                    customerId,
                                    payloads: ['activity'],
                                });
                            });
                    }

                    // Show link to client card
                    if (empty(this.$route) || this.$route.name !== 'leads.update') {
                        this.showLinkToClientCard = true;
                        this.callInProgress = true;
                    } else {
                        this.callInProgress = true;
                        this.$notify.success(this.$t('call.callInProgress'));
                        if (this.recordingDisabled || !this.callRecordingConsentScript) {
                            this.close();
                        }
                    }
                } catch (error) {
                    if (error.response) {
                        switch (error.response.status) {
                            case 403:
                                showWarning(this.$t('call.cantMakeCall'));
                                this.processing = false;
                                return;

                            case 404:
                                showWarning();
                                this.processing = false;
                                return;
                        }
                    }

                    this.appendNewError({
                        code: '0098',
                        display: true,
                        error,
                        payload,
                    });
                }

                this.processing = false;
            },

            async checkOldCommunication() {
                if (!this.$refs.modal?.exists) {
                    return;
                }

                const leadId = this.lead.id;
                const session = this.modalSession;
                const request = ++this.oldCommunicationRequest;
                const response = await this.$axios.get(
                    `v1/leadxpress/check-old-communication/${leadId}`
                );
                if (
                    session !== this.modalSession ||
                    request !== this.oldCommunicationRequest ||
                    leadId !== this.lead.id ||
                    !this.$refs.modal?.exists
                ) {
                    return;
                }

                this.mostRecentCommunication = empty(response.data.mostRecentCommunication)
                    ? null
                    : response.data.mostRecentCommunication;
            },

            invalidateOldCommunication() {
                this.modalSession += 1;
                this.oldCommunicationRequest += 1;
                this.mostRecentCommunication = null;
            },

            dismiss() {
                this.mostRecentCommunication = null;
            },

            async refreshLead() {
                const leadId = this.lead.id;
                const session = this.modalSession;
                const lead = await this.fetchLead(leadId);
                if (session === this.modalSession && leadId === this.lead.id) {
                    this.lead = lead;
                }
            },

            setLeadPhoneType() {
                if (this.customerContactTarget) {
                    this.leadPhone = this.customerContactTarget.contact_phone_id;
                    return;
                }

                if (this.specificPhoneToCall) {
                    this.lead.lead_phones.forEach((leadPhone) => {
                        if (this.specificPhoneToCall.id === leadPhone.id) {
                            this.leadPhone = leadPhone.id;
                        }
                    });
                } else if (this.lead.lead_phones.length === 1) {
                    this.leadPhone = this.lead.lead_phones[0].id;
                } else {
                    this.leadPhone = null;
                }
            },

            setUserPhone() {
                const phone = this.userPhones.find((p) => p.preferred) || this.userPhones[0];

                this.userPhone = phone?.id;
            },

            async startRecording() {
                try {
                    this.isRecording = true;
                    await this.$api.recordings.start(this.lead.id, this.lead.current_call_sid);
                    await this.refreshLead();
                } catch {
                    this.isRecording = false;
                    this.$notify.error(this.$t('call.recordingError'));
                }
            },

            async pauseRecording() {
                try {
                    this.isRecording = false;
                    await this.$api.recordings.pause(
                        this.lead.id,
                        this.lead.current_call_sid,
                        this.lead.current_call_recording_sid
                    );
                } catch {
                    this.isRecording = true;
                    this.$notify.error(this.$t('call.recordingError'));
                }
            },

            async resumeRecording() {
                try {
                    this.isRecording = true;
                    await this.$api.recordings.resume(
                        this.lead.id,
                        this.lead.current_call_sid,
                        this.lead.current_call_recording_sid
                    );
                } catch {
                    this.isRecording = false;
                    this.$notify.error(this.$t('call.recordingError'));
                }
            },

            async triggerCheckCommunicating() {
                this.checkingCommunicating = true;

                await this.checkCommunicating(this.lead.id);

                this.checkingCommunicating = false;
            },

            close() {
                this.invalidateOldCommunication();
                this.$refs.modal.hide();
            },

            endCall() {
                this.callInProgress = false;
                this.isRecording = false;

                this.close();
            },

            onOpen() {
                this.triggerCheckCommunicating();
                this.checkOldCommunication();
                this.setLeadPhoneType();
                this.setUserPhone();

                on('refresh-lead-call-sid', this.refreshLead);
                on('close-call-interface', this.endCall);
            },

            onClosed() {
                this.invalidateOldCommunication();
                this.leadId = null;
                this.customerContactTarget = null;
                this.lead = new Lead();
                this.currentUser = new User();
                this.showLinkToClientCard = false;
                this.showPhone = false;
                this.loading = true;
                this.recentCommunication = null;
                this.mostRecentCommunication = null;
                this.checkingCommunicating = false;
                this.setSpecificPhoneToCall(null);

                if (this.taskEventToBeCompleted) {
                    this.setTaskEventToBeCompleted(null);
                }

                off('refresh-lead-call-sid', this.refreshLead);
                off('close-call-interface', this.endCall);
            },

            async onBeforeOpen(params) {
                this.invalidateOldCommunication();
                const session = this.modalSession;
                const leadId = typeof params === 'object' ? params.leadId : params;
                this.leadId = leadId;
                this.customerContactTarget =
                    typeof params === 'object' ? params.customerContactTarget ?? null : null;

                const lead =
                    typeof params === 'object' && params.lead
                        ? params.lead
                        : await this.fetchLead(leadId);
                if (session !== this.modalSession) {
                    return;
                }
                if (
                    this.customerContactTarget &&
                    Number(this.customerContactTarget.lead_id) !== Number(lead.id)
                ) {
                    throw new Error('Customer Call target does not match modal Lead');
                }
                this.lead = lead;
                const currentUser =
                    (await this.parentAuthUser.childUserForAccount(lead.account_id)) ||
                    this.authUser;
                const userPhones = await currentUser.phones;
                if (session !== this.modalSession) {
                    return;
                }
                this.currentUser = currentUser;
                this.userPhones = userPhones;

                const callOwnedByCurrentUser =
                    this.lead.communicating_user_id === this.currentUser.id;

                if (!this.lead.current_call_sid && !callOwnedByCurrentUser) {
                    this.callInProgress = false;
                    this.isRecording = false;
                }

                this.onOpen();
                this.loading = false;
            },
        },
        beforeUnmount() {
            this.invalidateOldCommunication();
        },
    };
</script>
